/**
 * src/ui/styles.ts
 *
 * Define e injeta os estilos CSS necessários para o funcionamento
 * do componente de cenário interativo.
 *
 * Características:
 * - CSS totalmente isolado via prefixo global (`middag-app-cn-`)
 * - Nenhuma dependência de folhas de estilo externas
 * - Seguro para uso dentro do Moodle e temas customizados
 *
 * Estratégia:
 * - Os estilos são injetados dinamicamente via <style>
 * - A injeção ocorre apenas uma vez por página
 */

const PREFIX = 'middag-app-cn';

/**
 * CSS do componente.
 *
 * Observação:
 * - Mantido como string única para facilitar:
 *   - injeção dinâmica
 *   - versionamento
 *   - auditoria de impacto visual
 */
export const COMPONENT_STYLES = `
  /* =========================================================================
     CONTAINER BASE
     ========================================================================= */
  .${PREFIX}-container {
    position: relative;
    width: 100%;
    height: auto;
    overflow: hidden; /* Garante que hotspots não escapem da imagem */
    line-height: 0;
    background-color: #f0f0f0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }

  /* =========================================================================
     IMAGEM BASE (RESPONSIVA)
     ========================================================================= */
  .${PREFIX}-image-base {
    display: block;
    width: 100%;
    height: auto;
    user-select: none;
  }

  /* =========================================================================
     HOTSPOTS (INTERAÇÃO)
     ========================================================================= */
  .${PREFIX}-hotspot {
    position: absolute;
    z-index: 10;
    display: block;
    padding: 0;
    border: 0;
    outline: none;
    background-color: transparent; /* Necessário para capturar clique em polígonos */
    cursor: pointer;
    transition: transform 0.2s ease, filter 0.2s ease;
    touch-action: manipulation; /* Melhora resposta ao toque em mobile */
  }

  /* ÍCONES / PINS FLUTUANTES */
  .${PREFIX}-pin-image {
    display: block;
    width: 100%;       /* O tamanho é controlado pelo CSS do wrapper ou natural */
    max-width: 64px;   /* Evita pins gigantes */
    height: auto;
    /* Animação infinita de pulse */
    animation: ${PREFIX}-pin-pulse 2s infinite ease-in-out;
    transform-origin: center center;
  }

  /* Keyframes para o Pulse do Pin */
  @keyframes ${PREFIX}-pin-pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }

  .${PREFIX}-hotspot > i {
    color: white;
  }

  /* Hover / Focus visual (apenas em dispositivos com hover real) */
  @media (hover: hover) {
    /* Hover em hotspots normais (poligonos) */
    .${PREFIX}-hotspot:not(:has(.${PREFIX}-pin-image)):hover,
    .${PREFIX}-hotspot:not(:has(.${PREFIX}-pin-image)):focus-visible {
      /* Glow effect para polígonos */
      filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.9)) brightness(1.1);
      transform: scale(1.0001); /* Efeito de expandir, antes scale(1.02) */
    }
    
    /* Hover específico para Pins (pausa animação ou aumenta brilho) */
    .${PREFIX}-hotspot:hover .${PREFIX}-pin-image {
        filter: brightness(1.2);
    }
  }

  /* Acessibilidade: foco visível */
  .${PREFIX}-hotspot:focus-visible {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
    z-index: 20;
  }

  /* =========================================================================
     VARIANTES VISUAIS
     ========================================================================= */
  .${PREFIX}-hotspot--glass-door {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.2);
  }

  .${PREFIX}-hotspot--poster {
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.3);
  }

  .${PREFIX}-hotspot--tv-screen {
    /* Scanline effect leve opcional */
    background: linear-gradient(
      rgba(18, 16, 16, 0) 50%,
      rgba(0, 0, 0, 0.1) 50%
    );
    background-size: 100% 4px;
  }

  .${PREFIX}-hotspot--cool-effect {
    position: relative;
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.8), 0 0 10px rgba(147, 112, 219, 0.8), 0 0 15px rgba(64, 224, 208, 0.8);
    transition: all 0.3s ease-in-out;
  }

  .${PREFIX}-hotspot--cool-effect:hover {
    background-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.9), 0 0 20px rgba(147, 112, 219, 0.9), 0 0 30px rgba(64, 224, 208, 0.9);
    transform: scale(1.02);
  }

  /* =========================================================================
     ESTADO BLOQUEADO / DESBLOQUEADO (Moodle)
     ========================================================================= */
  .${PREFIX}-hotspot--locked {
    cursor: not-allowed;
    background-color: rgba(0, 0, 0, 0.4); /* Escurece para indicar bloqueio */
    border: 1px dashed rgba(255, 255, 255, 0.5);
    position: relative; /* Necessário para posicionar o ícone */
    font-family: 'Font Awesome 6 Free';
  }

  /* Ajuste para não aplicar fundo preto se for um PIN bloqueado */
  .${PREFIX}-hotspot--locked:has(.${PREFIX}-pin-image) {
    background-color: transparent;
    border: none;
    opacity: 0.5; /* Deixa o pin meio apagado */
    filter: grayscale(100%);
  }
  
  /* Remove o ícone padrão de cadeado se for um PIN (opcional, depende do design) */
  .${PREFIX}-hotspot--locked:has(.${PREFIX}-pin-image)::before {
    display: none; 
  }

  .${PREFIX}-hotspot--locked:not(:has(.${PREFIX}-pin-image))::before {
    content: "\\f023"; /* Ícone do Font Awesome para lock */
    font-family: 'Font Awesome 6 Free';
    font-weight: 900;
    color: #fff;
    font-size: var(--hotspot-icon-size, 1.5rem);
    position: absolute;
    top: var(--hotspot-cy, 50%);
    left: var(--hotspot-cx, 50%);
    transform: translate(-50%, -50%);
    animation: lockPulse 2s ease-in-out infinite;
    z-index: 2;
  }

  .${PREFIX}-hotspot--locked:hover {
    transform: none;
    filter: none;
  }

  .${PREFIX}-hotspot--unlocked {
    background-color: rgba(0, 0, 0, 0.3); /* Cor de fundo para indicar que está desbloqueado */
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
  }

  .${PREFIX}-hotspot--unlocked::before {
    content: "\\f09c"; /* Ícone do Font Awesome para unlocked */
    font-family: 'Font Awesome 6 Free';
    font-weight: 900;
    color: #fff;
    font-size: var(--hotspot-icon-size, 1.5rem);
    position: absolute;
    top: var(--hotspot-cy, 50%);
    left: var(--hotspot-cx, 50%);
    transform: translate(-50%, -50%);
    animation: unlockPulse 2s ease-in-out infinite;
    z-index: 2;
  }

  /* =========================================================================
     MODAL BASE
     ========================================================================= */
  .${PREFIX}-modal-layer {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none; /* Deixa passar clique quando hidden */
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .${PREFIX}-modal-layer--active {
    pointer-events: auto;
    opacity: 1;
  }

  .${PREFIX}-modal-backdrop {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.85);
  }

  .${PREFIX}-modal-content {
    position: relative;
    width: 100%;
    max-width: 100%;
    height: auto;
    /* Animação de Zoom será controlada via JS style transform */
    transform-origin: center center;
    transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  }

  /* Estilos para Modal de Texto */
  .${PREFIX}-modal-title {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    line-height: 2rem;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.5rem;
  }

  .${PREFIX}-modal-text-body {
    font-size: 1rem;
    line-height: 1.6;
    color: #555;
    max-height: 60vh; /* Permite scroll se texto for muito longo */
    overflow-y: auto;
  }

  .${PREFIX}-modal-text-body p {
    margin-bottom: 10px;
  }
  
  /* Scrollbar bonita para o modal de texto */
  .${PREFIX}-modal-text-body::-webkit-scrollbar {
    width: 8px;
  }
  .${PREFIX}-modal-text-body::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }

  .${PREFIX}-modal-close-btn {
    position: absolute;
    top: 2%;
    right: 2%;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border: 2px solid white;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Botão de fechar escuro para modal de fundo branco (texto) */
  .${PREFIX}-modal-content--text-mode .${PREFIX}-modal-close-btn {
     /* Ajusta posição para dentro do card branco */
     top: -20px;
     right: -20px;
     background: #333;
     border-color: #333;
  }

  .${PREFIX}-modal-close-btn:hover {
    background: rgba(255, 0, 0, 0.7);
  }

  /* =========================================================================
     MODO DEBUG (DX)
     ========================================================================= */
  .${PREFIX}-debug-mode .${PREFIX}-hotspot:not(:has(.${PREFIX}-pin-image)) {
    background-color: rgba(255, 0, 0, 0.6) !important;
    border: 1px dashed red !important;
    animation: ${PREFIX}-pulse 1s infinite;
  }

  @keyframes ${PREFIX}-pulse {
    0% { opacity: 0.4; }
    50% { opacity: 0.8; }
    100% { opacity: 0.4; }
  }

  /* =========================================================================
     LOADER / ERRO
     ========================================================================= */
  .${PREFIX}-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #666;
    font-size: 14px;
    text-align: center;
  }

  /* =========================================================================
     ANIMAÇÕES
     ========================================================================= */

  /* Animação de ícones de bloqueio */
  @keyframes lockPulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: .9;
      filter: drop-shadow(0 0 2px rgba(255,255,255,.5))
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 1;
      filter: drop-shadow(0 0 8px rgba(255,255,255,.8))
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: .9;
      filter: drop-shadow(0 0 2px rgba(255,255,255,.5))
    }
  }

  /* Animação de ícones de desbloqueio */
  @keyframes unlockPulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: .9;
      filter: drop-shadow(0 0 2px rgba(0, 0, 0,.5))
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 1;
      filter: drop-shadow(0 0 8px rgba(0, 0, 0,.8))
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: .9;
      filter: drop-shadow(0 0 2px rgba(0, 0, 0,.5))
    }
  }

  /* =========================================================================
     RESPONSIVO (MOBILE): reduz tamanho dos ícones Font Awesome nos hotspots
     - Ajuste o breakpoint e os valores conforme seu layout
     ========================================================================= */
  @media (max-width: 960px) {
    .${PREFIX}-hotspot--locked:not(:has(.${PREFIX}-pin-image))::before {
      font-size: 1rem;
    }
  
    .${PREFIX}-hotspot--unlocked::before {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    .${PREFIX}-hotspot--locked:not(:has(.${PREFIX}-pin-image))::before {
      font-size: 0.8rem;
    }
  
    .${PREFIX}-hotspot--unlocked::before {
      font-size: 0.8rem;
    }
  }


`;

/**
 * Injeta os estilos do componente no <head>.
 *
 * Observação:
 * - A função é idempotente.
 * - Se o estilo já existir, não injeta novamente.
 */
export function injectStyles(): void {
  if (document.getElementById(`${PREFIX}-style`)) return;

  const style = document.createElement('style');
  style.id = `${PREFIX}-style`;
  style.textContent = COMPONENT_STYLES;
  document.head.appendChild(style);
}
